/**
 * Bundle 142 - Registrations landing page (event-scoped).
 *
 * Route: /admin/registrations
 *
 * User request: separate "Registrations" (event-scoped) from
 * "Users" (cross-app). Pick an event first, then drill into the
 * pre-existing AdminRegistrationsPage at
 * `/admin/events/:id/registrations`.
 *
 * Existing per-event URL bookmarks continue to work; this page is
 * the new landing surface.
 *
 * Guardrails: no em/en dashes; no eslint-disable; no photo/image
 * in HTML alt attributes.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Users, CalendarDays, ArrowRight, Search, Ticket } from 'lucide-react';

const AdminRegistrationsLandingPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  // Bundle 164 C - per-event counts + gender split. Fetched
  // via a new aggregation endpoint so the shared /admin/events
  // response is untouched. FE merges by event_id.
  const [counts, setCounts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, ct] = await Promise.all([
        api.get('/admin/events?limit=200'),
        api.get('/admin/events-registration-counts').catch(
          () => ({ data: { counts: {} } })),
      ]);
      const list = Array.isArray(ev.data)
        ? ev.data : (ev.data?.events || []);
      setEvents(list);
      setCounts(ct.data?.counts || {});
    } catch (_) {
      setEvents([]);
      setCounts({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return events.filter((e) => {
      if (!needle) return true;
      const hay = (
        (e.title || '') + ' '
        + (e.slug || '') + ' '
        + (e.city || '')
      ).toLowerCase();
      return hay.includes(needle);
    });
  }, [events, q]);

  return (
    <div
      className="p-6 max-w-6xl mx-auto"
      data-testid="admin-registrations-landing-root"
    >
      <div className="mb-6">
        <h1
          className="text-2xl font-bold text-neutral-900 flex items-center gap-2"
          data-testid="admin-registrations-landing-title"
        >
          <Ticket className="h-6 w-6 text-primary-700" />
          Registrations
        </h1>
        <p
          className="text-sm text-neutral-700 mt-1"
          data-testid="admin-registrations-landing-purpose"
        >
          Event-scoped attendee list. Pick an event below to view,
          refund, transfer tickets, or manually check-in registrants.
          For the cross-app user directory (regardless of event),
          open {' '}
          <Link
            to="/admin/users"
            className="text-primary-700 underline"
            data-testid="admin-registrations-landing-users-link"
          >
            Users
          </Link>.
        </p>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 flex items-center gap-2">
          <Search className="h-4 w-4 text-neutral-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by title, slug, or city..."
            data-testid="admin-registrations-landing-search"
            className="border-none focus-visible:ring-0"
          />
          <Badge variant="outline">
            {filtered.length} / {events.length} events
          </Badge>
        </CardContent>
      </Card>

      {loading ? (
        <div
          className="p-6 text-neutral-500"
          data-testid="admin-registrations-landing-loading"
        >
          Loading events...
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="p-6 text-neutral-700 text-center"
          data-testid="admin-registrations-landing-empty"
        >
          No events match the current filter.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((ev) => {
            // Bundle 164 hotfix 2 - zero-count fallback. Events
            // with no active registrations do not appear in the
            // aggregation response; treat missing key as zeros
            // so every card renders the pills row (0 is a real
            // state, not a load failure).
            const c = counts[ev.id] || {
              count: 0,
              gender: { male: 0, female: 0, other: 0, unknown: 0 },
            };
            const total = c.count ?? 0;
            const g = c.gender || {};
            return (
              <Card
                key={ev.id}
                data-testid={`admin-registrations-event-card-${ev.id}`}
              >
                <CardContent className="p-4 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-neutral-900">
                        {ev.title || ev.slug || ev.id}
                      </div>
                      <div className="text-xs text-neutral-700 flex items-center gap-2 mt-1">
                        {ev.city ? (
                          <span className="inline-flex items-center gap-1">
                            <CalendarDays className="h-3 w-3" />
                            {ev.city}
                          </span>
                        ) : null}
                        {ev.state ? (
                          <Badge variant="outline">{ev.state}</Badge>
                        ) : null}
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      data-testid={`admin-registrations-event-count-${ev.id}`}
                    >
                      {total} regs
                    </Badge>
                  </div>
                  <div
                    className="flex flex-wrap items-center gap-1 text-[10px] text-neutral-600"
                    data-testid={`admin-registrations-event-gender-${ev.id}`}
                  >
                    <Badge
                      variant="outline"
                      className="bg-primary-50 text-primary-800 border-primary-200 px-1.5"
                      data-testid={`admin-registrations-event-male-${ev.id}`}
                    >
                      M: {g.male || 0}
                    </Badge>
                    <span className="text-neutral-400">&middot;</span>
                    <Badge
                      variant="outline"
                      className="bg-secondary-50 text-secondary-800 border-secondary-200 px-1.5"
                      data-testid={`admin-registrations-event-female-${ev.id}`}
                    >
                      F: {g.female || 0}
                    </Badge>
                    {g.other ? (
                      <>
                        <span className="text-neutral-400">&middot;</span>
                        <Badge
                          variant="outline"
                          className="bg-neutral-50 text-neutral-800 border-neutral-200 px-1.5"
                          data-testid={`admin-registrations-event-other-${ev.id}`}
                        >
                          Other: {g.other}
                        </Badge>
                      </>
                    ) : null}
                    {g.unknown ? (
                      <>
                        <span className="text-neutral-400">&middot;</span>
                        <Badge
                          variant="outline"
                          className="bg-neutral-50 text-neutral-700 border-neutral-200 px-1.5"
                          data-testid={`admin-registrations-event-unknown-${ev.id}`}
                        >
                          Unknown: {g.unknown}
                        </Badge>
                      </>
                    ) : null}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 justify-between"
                    onClick={() => navigate(
                      `/admin/events/${ev.id}/registrations`)}
                    data-testid={`admin-registrations-event-cta-${ev.id}`}
                  >
                    Open registrations
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminRegistrationsLandingPage;
